import React, { useContext, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Link, Paper, TextField, Typography } from '@mui/material';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import LabelPreview from './LabelPreview';
import { labelPreviewVariables, useLabelPreview } from '../hooks/useWarehouseLabelConfig';
import { LabelPreviewResult, LabelTemplateSlug, TemplateDraft } from '../model';

// Mirrors Warehouse::LabelConfig::FALLBACK_SLUG: a department no template claims prints this.
const FALLBACK_SLUG: LabelTemplateSlug = 'everything_else';

interface PoLabelCheckerProps {
  location: string;
  drafts: Record<string, TemplateDraft>;
  codeOwner: Record<string, LabelTemplateSlug>;
  templateNames: Record<string, string>;
  onOpenTemplate: (slug: LabelTemplateSlug) => void;
}

const PoLabelChecker: React.FC<PoLabelCheckerProps> = ({
  location,
  drafts,
  codeOwner,
  templateNames,
  onOpenTemplate,
}) => {
  const [poNumber, setPoNumber] = useState('');
  const [result, setResult] = useState<LabelPreviewResult | null>(null);
  const preview = useLabelPreview();
  const { showErrorSnackBar } = useContext(SnackBarContext);

  // The field as typed right now, read when a check returns: state captured by handleCheck would
  // still hold the value from when the check started.
  const latestPo = useRef('');

  // Any edit makes the shown label belong to a PO that is no longer in the field.
  const handlePoChange = (value: string) => {
    latestPo.current = value;
    setPoNumber(value);
    setResult(null);
  };

  const handleCheck = async () => {
    const po = poNumber.trim();
    if (!po || preview.isPending) return;
    // Cleared first so the previous PO's label never sits under a new PO number.
    setResult(null);
    // A check that returns after the field was edited is for a PO nobody is looking at anymore,
    // so neither its label nor its error is shown.
    const stillSelected = () => latestPo.current.trim() === po;
    try {
      const response = await preview.mutateAsync(labelPreviewVariables(po, location));
      if (stillSelected()) setResult(response.data ?? null);
    } catch {
      if (stillSelected()) showErrorSnackBar?.('Could not check that PO.');
    }
  };

  // Resolved from the drafts, not the server, so an unsaved department move or body edit shows
  // here immediately - the point is to adapt the template and watch the real label change.
  const slug = (result?.department_code && codeOwner[result.department_code]) || FALLBACK_SLUG;
  const body = drafts[slug]?.body ?? '';

  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Check a PO
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label="PO number"
          size="small"
          value={poNumber}
          onChange={(e) => handlePoChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
          sx={{ maxWidth: 200 }}
        />
        <Button
          variant="outlined"
          size="small"
          disabled={!poNumber.trim() || preview.isPending}
          startIcon={preview.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={handleCheck}
        >
          Check
        </Button>
      </Box>

      {result?.error && <ErrorMessage message={result.error} />}

      {result && !result.error && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            PO {result.po_number} — {result.department ?? 'no department'}
            {result.department_code ? ` (${result.department_code})` : ''} — prints with{' '}
            <Link component="button" variant="body2" sx={{ fontWeight: 600 }} onClick={() => onOpenTemplate(slug)}>
              {templateNames[slug] ?? slug}
            </Link>
          </Typography>
          <LabelPreview body={body} values={result.values} />
        </Box>
      )}
    </Paper>
  );
};

export default PoLabelChecker;
